<template>
  <div class="legal-page">
    <!-- Monogram Background Layer -->
    <div class="monogram-wrapper">
      <div class="monogram-background"></div>
    </div>

    <!-- Header Hero Section -->
    <section class="legal-hero">
      <div class="container">
        <div class="hero-content">
          <span class="hero-badge">{{ t('contact.badge') || '// DIRECT CHANNEL' }}</span>
          <h1 class="hero-title">{{ t('contact.title') }}</h1>
          <div class="hero-divider"></div>
          <p class="last-update">{{ t('contact.subtitle') }}</p>
        </div>
      </div>
    </section>

    <!-- Content Section -->
    <section class="legal-body">
      <div class="container">
        <div class="legal-layout">
          <!-- Sidebar Navigation (Optional for Contact, but kept for consistency) -->
          <aside class="legal-sidebar desktop-only">
            <div class="sidebar-sticky">
              <nav class="side-nav">
                <a href="#form" class="nav-anchor">
                  <span class="nav-num">01</span>
                  <span class="nav-text">{{ t('form.label.message') }}</span>
                </a>
              </nav>
            </div>
          </aside>

          <!-- Main Legal Content -->
          <div class="legal-main">
            <div id="form" class="legal-section-card">
              <div class="card-inner">
                <div class="section-header">
                  <span class="section-index">// 01</span>
                  <h2>{{ t('contact.title') }}</h2>
                </div>
                
                <div class="section-content">
                  <form v-if="!formSubmitted" class="contact-form" @submit.prevent="submitForm">
                    <div class="form-row">
                      <div class="form-group">
                        <label for="name">{{ t('form.label.name') }}</label>
                        <input 
                          id="name" 
                          v-model.trim="form.name" 
                          type="text" 
                          class="form-control"
                          :placeholder="t('form.placeholder.name')"
                          required
                        >
                      </div>

                      <div class="form-group">
                        <label for="email">{{ t('form.label.email') }}</label>
                        <input 
                          id="email" 
                          v-model.trim="form.email" 
                          type="email" 
                          class="form-control"
                          :placeholder="t('form.placeholder.email')"
                          required
                        >
                      </div>
                    </div>

                    <div class="form-group">
                      <label for="subject">{{ t('form.label.subject') }}</label>
                      <input 
                        id="subject" 
                        v-model.trim="form.subject" 
                        type="text" 
                        class="form-control"
                        :placeholder="t('form.placeholder.subject')"
                        required
                      >
                    </div>

                    <div class="form-group">
                      <label for="message">{{ t('form.label.message') }}</label>
                      <textarea 
                        id="message" 
                        v-model="form.message" 
                        class="form-control"
                        rows="6"
                        :placeholder="t('form.placeholder.message')"
                        required
                      ></textarea>
                    </div>

                    <div class="privacy-checkbox-group">
                      <input
                        type="checkbox"
                        id="privacy-check"
                        v-model="privacyAccepted"
                        required
                      />
                      <label for="privacy-check">
                        {{ t('form.privacy.before_link') }}
                        <router-link to="/privacy-policy" target="_blank" class="legal-link">
                          {{ t('footer.nav.legal.privacy') }}
                        </router-link>
                        {{ t('form.privacy.after_link') }}
                      </label>
                    </div>

                    <button type="submit" class="btn-submit" :disabled="isSubmitting || !privacyAccepted">
                      {{ isSubmitting ? '...' : t('form.button.submit') }}
                    </button>

                    <!-- Legal notice for reCAPTCHA -->
                    <div class="recaptcha-notice" v-html="t('form.recaptcha_notice', { 
                      privacy: `<a href='https://policies.google.com/privacy' target='_blank'>${t('form.google_privacy')}</a>`, 
                      terms: `<a href='https://policies.google.com/terms' target='_blank'>${t('form.google_terms')}</a>` 
                    })"></div>
                  </form>
                  
                  <div v-if="formSubmitted" class="success-message">
                    <div class="success-text">
                      <h3>{{ t('form.success.msg') }}</h3>
                      <button @click="formSubmitted = false" class="btn-reset">{{ t('auth.forgot.back_link') }}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useToast } from '@/composables/useToast'
import { useLanguage } from '@/composables/useLanguage'
import { useConfigStore } from '@/stores/configStore'
import apiClient from '@/services/api'

const { t } = useLanguage()
const toast = useToast()
const configStore = useConfigStore()
const formSubmitted = ref(false)
const isSubmitting = ref(false)
const privacyAccepted = ref(false)

const form = ref({
  name: '',
  email: '',
  subject: '',
  message: ''
})

const submitForm = async () => {
  if (!privacyAccepted.value) return
  
  const nameRegex = /^[\p{L}\p{M}' \.\-]+$/u;
  const fullName = form.value.name.trim();

  if (fullName.length < 2 || !nameRegex.test(fullName)) {
    toast.error(t('form.error.invalid_name'))
    return
  }
  
  isSubmitting.value = true
  
  try {
    const recaptchaToken = await generateRecaptchaToken()
    if (!recaptchaToken) {
      toast.error(t('apiCodes.CONTACT_RECAPTCHA_REQUIRED'))
      return
    }
    const payload = {
      fullName: form.value.name,
      email: form.value.email,
      subject: form.value.subject,
      message: form.value.message,
      acceptPrivacyPolicy: privacyAccepted.value,
      recaptchaToken
    }
    
    await apiClient.post('/Contact', payload)
    
    formSubmitted.value = true
    toast.success(t('form.success.msg'))
    
    form.value = {
      name: '',
      email: '',
      subject: '',
      message: ''
    }
    privacyAccepted.value = false
  } catch (error) {
    console.error('[Contact] Error sending message:', error)
    const errorMsg = error.response?.data?.message || t('toast.error.generic')
    toast.error(errorMsg)
  } finally {
    isSubmitting.value = false
  }
}

const generateRecaptchaToken = async () => {
  if (!configStore.recaptchaSiteKey) return ''
  
  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        resolve(true)
        return
      }
      
      let attempts = 0
      const interval = setInterval(() => {
        attempts++
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval)
          resolve(true)
        } else if (attempts > 50) {
          clearInterval(interval)
          resolve(false)
        }
      }, 100)
    })
  }

  const isRecaptchaReady = await waitForRecaptcha()
  if (!isRecaptchaReady) return ''

  return await new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(configStore.recaptchaSiteKey, { action: 'contact' })
        resolve(token)
      } catch (e) {
        console.error('[RECAPTCHA] Execute error:', e)
        resolve('')
      }
    })
  })
}
</script>

<style scoped>
.legal-page {
  background-color: #ffffff;
  color: #000;
  min-height: 100vh;
  font-family: 'Montserrat', sans-serif;
  position: relative;
  overflow-x: hidden;
}

.monogram-wrapper {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.monogram-background {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background-image: url("@/assets/images/logo.png");
  background-size: 120px auto;
  background-repeat: repeat;
  opacity: 0.22;
  transform: rotate(-15deg);
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 25px;
  position: relative;
  z-index: 1;
}

.legal-hero {
  padding: 40px 0 25px;
  background-color: #000;
  text-align: center;
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: block;
  font-size: 9px;
  font-weight: 800;
  color: var(--bright-teal);
  letter-spacing: 3px;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.hero-title {
  font-size: clamp(22px, 3vw, 28px);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.5px;
  margin-bottom: 12px;
  line-height: 1.1;
  color: #fff;
}

.hero-divider {
  width: 30px;
  height: 2px;
  background-color: var(--bright-teal);
  margin: 0 auto 12px;
}

.last-update {
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  font-style: italic;
}

.legal-body {
  padding: 60px 0 100px;
  position: relative;
  z-index: 1;
}

.legal-layout {
  display: flex;
  gap: 50px;
}

@media (max-width: 992px) {
  .legal-layout {
    flex-direction: column;
    gap: 25px;
  }
}

.legal-sidebar {
  flex: 0 0 240px;
  position: relative;
  z-index: 2;
}

.sidebar-sticky {
  position: sticky;
  top: 110px;
  background-color: #fff;
  padding: 25px 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03);
}

.side-nav {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.nav-anchor {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 15px;
  text-decoration: none;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.nav-num {
  font-family: 'Inter', sans-serif;
  font-size: 9px;
  font-weight: 800;
  color: var(--dark-teal);
  margin-top: 2px;
}

.nav-text {
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.nav-anchor:hover .nav-text {
  color: var(--dark-teal);
}

.nav-anchor:hover {
  border-left-color: var(--dark-teal);
  background: rgba(0, 0, 0, 0.02);
}

.legal-main {
  flex: 1;
}

.legal-section-card {
  margin-bottom: 30px;
}

.card-inner {
  background-color: #000;
  padding: 40px;
  border-radius: 4px;
  border-left: 4px solid var(--bright-teal);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.section-header {
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 15px;
}

.section-index {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 800;
  color: var(--bright-teal);
  margin-bottom: 5px;
  letter-spacing: 2px;
}

.section-header h2 {
  font-size: 18px;
  font-weight: 800;
  text-transform: uppercase;
  color: #fff;
}

/* Form Styles Override */
.contact-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.06);
  color: #ffffff !important;
  caret-color: var(--bright-teal);
  -webkit-text-fill-color: #ffffff;
}

.form-control::placeholder {
  color: rgba(255, 255, 255, 0.45);
  opacity: 1;
}

.form-control:focus {
  outline: none;
  border-color: var(--bright-teal);
  background-color: rgba(255, 255, 255, 0.10);
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff;
}

.form-control:-webkit-autofill,
.form-control:-webkit-autofill:hover,
.form-control:-webkit-autofill:focus {
  -webkit-text-fill-color: #ffffff !important;
  -webkit-box-shadow: 0 0 0 1000px rgba(20, 20, 20, 0.95) inset !important;
  box-shadow: 0 0 0 1000px rgba(20, 20, 20, 0.95) inset !important;
  caret-color: var(--bright-teal);
  transition: background-color 9999s ease-in-out 0s;
}

.privacy-checkbox-group {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
}

.privacy-checkbox-group input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  accent-color: var(--bright-teal);
}

.privacy-checkbox-group label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
}

.legal-link {
  color: var(--bright-teal);
  text-decoration: underline;
}

.btn-submit {
  margin-top: 20px;
  padding: 18px;
  background-color: var(--bright-teal);
  color: #000;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-submit:hover:not(:disabled) {
  background-color: #fff;
  transform: translateY(-2px);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recaptcha-notice {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 15px;
}

.success-message {
  text-align: center;
  padding: 40px 0;
}

.success-text h3 {
  color: var(--bright-teal);
  font-size: 18px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 30px;
}

.btn-reset {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 20px;
  color: #fff;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  border-radius: 4px;
}

.legal-sidebar {
  flex: 0 0 240px;
  position: relative;
  z-index: 2;
}

@media (max-width: 992px) {
  .legal-sidebar {
    flex: 0 0 auto;
    width: 100%;
  }
  .sidebar-sticky {
    position: static;
  }
}

@media (max-width: 768px) {
  .legal-sidebar {
    display: none !important;
  }
  .form-row {
    grid-template-columns: 1fr;
  }
  .card-inner {
    padding: 22px 18px;
  }
  .legal-body {
    padding: 30px 0 60px;
  }
  .legal-hero {
    padding: 25px 0 18px;
  }
  .container {
    padding: 0 16px;
  }
  .form-control {
    padding: 11px 13px;
    font-size: 14px;
  }
  .btn-submit {
    padding: 15px;
    font-size: 12px;
  }
  .monogram-background {
    background-size: 60px auto;
  }
  .section-header h2 {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .legal-body {
    padding: 20px 0 50px;
  }
  .card-inner {
    padding: 18px 14px;
  }
  .hero-title {
    font-size: 20px;
  }
  .container {
    padding: 0 12px;
  }
}
</style>
